import Vue from "vue";
import createAuth0Client from "@auth0/auth0-spa-js";

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

/** Clears auth0-spa-js's own cache/transaction storage. Used to recover from
 * a stale/corrupted session (e.g. leftover state from a client_id/scope/
 * audience change, or an abandoned login attempt) that otherwise blocks
 * login indefinitely and previously required the user to manually run
 * localStorage.clear() in devtools - not viable on mobile. */
const clearAuth0Cache = () => {
  [window.localStorage, window.sessionStorage].forEach(store => {
    Object.keys(store)
      .filter(k => k.startsWith('@@auth0spajs@@') || k.startsWith('a0.spajs.txs.'))
      .forEach(k => store.removeItem(k));
  });
};

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null
      };
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      async loginWithRedirect(o) {
        try {
          return await this.auth0Client.loginWithRedirect(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error('loginWithRedirect failed, clearing stale Auth0 cache and retrying', e);
          clearAuth0Cache();
          return this.auth0Client.loginWithRedirect(o);
        }
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      }
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      const clientOptions = {
        domain: options.domain,
        client_id: options.clientId,
        audience: options.audience,
        useRefreshTokens: options.useRefreshTokens,
        cacheLocation: options.cacheLocation,
        redirect_uri: redirectUri
      };
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client(clientOptions);

      try {
        // If the user is returning to the app after authentication..
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        // A stale/corrupted cache entry (e.g. left over from a previous
        // client_id/scope/audience) can make the redirect callback fail.
        // Clear it and re-create the client so the user ends up in a clean
        // logged-out state instead of stuck - this previously required a
        // manual localStorage.clear() in devtools, not viable on mobile.
        this.error = e;
        clearAuth0Cache();
        this.auth0Client = await createAuth0Client(clientOptions);
      } finally {
        // Initialize our internal authentication state
        try {
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.user = await this.auth0Client.getUser();
        } catch (e) {
          clearAuth0Cache();
          this.isAuthenticated = false;
        }
        this.loading = false;
      }
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};
